// cashSettlementApi.js - API utilities for cash management operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      return tokens.idToken.toString();
    } else {
      console.error('No ID Token found in the current session.');
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error('Authentication token is missing.');
    error.code = 'AUTHENTICATION_FAILED';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Cash Management API: Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Cash Management API Error: ${response.status} ${response.statusText}`, errorData);

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body?.error || errorData.error || 'Unknown error'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_cash_settlement',
        `Cash Settlement API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[cashSettlementApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      return data;
    }
    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }
    console.error(`Cash Management API: Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_cash_settlement',
      `Cash Settlement API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[cashSettlementApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * Cash Management API service
 */
export const cashSettlementApi = {
  // ==================== Cash Shifts ====================

  /**
   * Create Cash Shift
   * POST /cash/shifts
   * @param {Object} shiftData - Cash shift data
   * @param {string} shiftData.ss_id - Unique shift ID (required)
   * @param {string} shiftData.site_id - Site ID (required)
   * @param {number} shiftData.opening_float - Opening float amount
   * @param {number} shiftData.total_cash_sales - Total cash sales
   * @param {number} shiftData.manual_paid_out_amount - Manual paid out amount
   * @param {string} shiftData.paid_out_reason - Paid out reason
   * @param {string} shiftData.manager_signature_digital - Manager signature (base64 or URL)
   * @param {string} shiftData.status - Status: "open" or "closed"
   * @returns {Promise<Object>} Response object with success status and data
   */
  createCashShift: async (shiftData) => {
    try {
      const response = await makeApiRequest('/cash/shifts', 'POST', shiftData);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error creating cash shift:', error);
      return {
        success: false,
        error: error.message || 'Failed to create cash shift',
      };
    }
  },

  /**
   * Get Cash Shift (primary source for settlement summary)
   * GET /cash/shifts/{ss_id}
   * Lambda: innoworx-cash-shift-get
   * @param {string} ssId - Shift ID (ss_id)
   * @returns {Promise<Object>} Response object with success status and cash shift data (opening_float, total_cash_sales, manual_paid_out_amount, etc.)
   */
  getCashShift: async (ssId) => {
    try {
      const response = await makeApiRequest(`/cash/shifts/${ssId}`, 'GET');
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error getting cash shift:', error);
      return {
        success: false,
        error: error.message || 'Failed to get cash shift',
      };
    }
  },

  /**
   * Get current shift data from attendance (GET /attendance/get-current/v2).
   * Returns a single current shift in the same shape as list/get shift for compatibility.
   * @returns {Promise<Object>} { success, shift, shifts } - shift and shifts (array of one or empty)
   */
  getCurrentShiftData: async () => {
    try {
      const att = await cashSettlementApi.fetchCurrentAttendance();
      const active = att?.active_shift;
      const ssId = att?.ss_id ?? active?.ss_id;
      const siteId = att?.site_id ?? active?.site_id;
      const shift = active
        ? { ...active, ss_id: active.ss_id || ssId, site_id: active.site_id || siteId }
        : ssId
          ? { ss_id: ssId, site_id: siteId, ...att }
          : null;
      return {
        success: !!shift,
        shift: shift || undefined,
        shifts: shift ? [shift] : [],
      };
    } catch (error) {
      console.error(' cashSettlementApi.getCurrentShiftData: Error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get current shift from attendance',
        shifts: [],
      };
    }
  },

  /**
   * Update Cash Shift
   * PUT /cash/shifts/{ss_id}
   * @param {string} ssId - Shift ID
   * @param {Object} shiftData - Updated cash shift data
   * @returns {Promise<Object>} Response object with success status and data
   */
  updateCashShift: async (ssId, shiftData) => {
    try {
      console.log(' cashSettlementApi.updateCashShift: Calling API for shift ID:', ssId);
      console.log(' cashSettlementApi.updateCashShift: Update data:', shiftData);
      const response = await makeApiRequest(`/cash/shifts/${ssId}`, 'PUT', shiftData);
      console.log(' cashSettlementApi.updateCashShift: Raw API response:', response);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(' cashSettlementApi.updateCashShift: Error:', error);
      console.error(' cashSettlementApi.updateCashShift: Error details:', {
        message: error.message,
        status: error.status,
        endpoint: error.endpoint,
        responseData: error.responseData,
      });
      return {
        success: false,
        error: error.message || 'Failed to update cash shift',
      };
    }
  },

  // ==================== Drawer Counts ====================

  /**
   * Create Drawer Count
   * POST /cash/drawers
   * @param {Object} drawerData - Drawer count data
   * @param {string} drawerData.ss_id - Shift ID (required)
   * @param {string} drawerData.drawer_id - Drawer ID (e.g., "drawer-1")
   * @param {Object} drawerData.denominations - Denominations object with keys as string values
   * @param {number} drawerData.total_amount - Total amount
   * @returns {Promise<Object>} Response object with success status and data
   */
  createDrawerCount: async (drawerData) => {
    try {
      console.log(' cashSettlementApi.createDrawerCount: Calling API with data:', drawerData);
      const response = await makeApiRequest('/cash/drawers', 'POST', drawerData);
      console.log(' cashSettlementApi.createDrawerCount: Raw API response:', response);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(' cashSettlementApi.createDrawerCount: Error:', error);
      console.error(' cashSettlementApi.createDrawerCount: Error details:', {
        message: error.message,
        status: error.status,
        endpoint: error.endpoint,
        responseData: error.responseData,
      });
      return {
        success: false,
        error: error.message || 'Failed to create drawer count',
      };
    }
  },

  /**
   * List Drawer Counts
   * GET /cash/drawers?ss_id={ss_id}
   * @param {string} ssId - Shift ID
   * @returns {Promise<Object>} Response object with success status and drawer counts array
   */
  listDrawerCounts: async (ssId) => {
    try {
      const response = await makeApiRequest(`/cash/drawers?ss_id=${ssId}`, 'GET');
      console.log('GET /cash/drawers response:', JSON.stringify(response, null, 2));
      return {
        success: true,
        drawerCounts: response.drawer_counts || response.drawers || response.data || [],
      };
    } catch (error) {
      console.error('Error listing drawer counts:', error);
      return {
        success: false,
        error: error.message || 'Failed to list drawer counts',
        drawerCounts: [],
      };
    }
  },

  /**
   * Get Drawer Count
   * GET /cash/drawers/{drawer_count_id}
   * @param {string} drawerCountId - Drawer count ID
   * @returns {Promise<Object>} Response object with success status and drawer count data
   */
  getDrawerCount: async (drawerCountId) => {
    try {
      const response = await makeApiRequest(`/cash/drawers/${drawerCountId}`, 'GET');
      return {
        success: true,
        drawerCount: response.drawer || response.data || response,
      };
    } catch (error) {
      console.error('Error fetching drawer count:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch drawer count',
      };
    }
  },

  /**
   * Delete Drawer Count
   * DELETE /cash/drawers/{drawer_count_id}
   * @param {string} drawerCountId - Drawer count ID
   * @returns {Promise<Object>} Response object with success status
   */
  deleteDrawerCount: async (drawerCountId) => {
    try {
      const response = await makeApiRequest(`/cash/drawers/${drawerCountId}`, 'DELETE');
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error deleting drawer count:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete drawer count',
      };
    }
  },

  // Legacy method for backward compatibility
  getDrawerCounts: async (shiftId) => {
    return cashSettlementApi.listDrawerCounts(shiftId);
  },

  // ==================== Paid-outs ====================

  /**
   * Create Paid-out
   * POST /cash/paid-outs
   * @param {Object} paidOutData - Paid-out data
   * @param {string} paidOutData.ss_id - Shift ID (required)
   * @param {number} paidOutData.amount - Amount (required)
   * @param {string} paidOutData.paid_out_reason - Paid out reason (required)
   * @param {string} paidOutData.notes - Notes
   * @param {string} paidOutData.approved_by - Approver user ID
   * @returns {Promise<Object>} Response object with success status and data
   */
  createPaidOut: async (paidOutData) => {
    try {
      const response = await makeApiRequest('/cash/paid-outs', 'POST', paidOutData);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error creating paid-out:', error);
      return {
        success: false,
        error: error.message || 'Failed to create paid-out',
      };
    }
  },

  /**
   * List Paid-outs
   * GET /cash/paid-outs?ss_id={ss_id}
   * @param {string} ssId - Shift ID
   * @returns {Promise<Object>} Response object with success status and paid-outs array
   */
  listPaidOuts: async (ssId) => {
    try {
      const response = await makeApiRequest(`/cash/paid-outs?ss_id=${ssId}`, 'GET');
      console.log('GET /cash/paid-outs response:', JSON.stringify(response, null, 2));
      return {
        success: true,
        paidOuts: response.paid_outs || response.data || [],
      };
    } catch (error) {
      console.error('Error listing paid-outs:', error);
      return {
        success: false,
        error: error.message || 'Failed to list paid-outs',
        paidOuts: [],
      };
    }
  },

  /**
   * Get Paid-out
   * GET /cash/paid-outs/{paid_out_id}
   * @param {string} paidOutId - Paid-out ID
   * @returns {Promise<Object>} Response object with success status and paid-out data
   */
  getPaidOut: async (paidOutId) => {
    try {
      const response = await makeApiRequest(`/cash/paid-outs/${paidOutId}`, 'GET');
      return {
        success: true,
        paidOut: response.paid_out || response.data || response,
      };
    } catch (error) {
      console.error('Error fetching paid-out:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch paid-out',
      };
    }
  },

  /**
   * Delete Paid-out
   * DELETE /cash/paid-outs/{paid_out_id}
   * @param {string} paidOutId - Paid-out ID
   * @returns {Promise<Object>} Response object with success status
   */
  deletePaidOut: async (paidOutId) => {
    try {
      const response = await makeApiRequest(`/cash/paid-outs/${paidOutId}`, 'DELETE');
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error deleting paid-out:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete paid-out',
      };
    }
  },

  // Legacy method for backward compatibility
  getPaidOuts: async (shiftId) => {
    return cashSettlementApi.listPaidOuts(shiftId);
  },

  // Legacy method for backward compatibility
  updatePaidOut: async (paidOutId, paidOutData) => {
    // Note: Update endpoint not in spec, but keeping for backward compatibility
    try {
      const response = await makeApiRequest(`/cash/paid-outs/${paidOutId}`, 'PUT', paidOutData);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error updating paid-out:', error);
      return {
        success: false,
        error: error.message || 'Failed to update paid-out',
      };
    }
  },

  // ==================== Safe Drop (Legacy) ====================

  /**
   * Create Safe Drop (Legacy - maps to drawer count)
   * POST /cash/drawers (via createDrawerCount)
   * @param {Object} safeDropData - Safe drop data
   * @returns {Promise<Object>} Response object with success status
   */
  createSafeDrop: async (safeDropData) => {
    // Map safe drop data to drawer count format
    const denominations = {};
    if (safeDropData.denominations && Array.isArray(safeDropData.denominations)) {
      safeDropData.denominations.forEach((denom) => {
        denominations[denom.value.toString()] = denom.count || 0;
      });
    }

    const drawerData = {
      ss_id: safeDropData.ss_id,
      drawer_id: safeDropData.drawer_id || 'drawer-1',
      denominations: denominations,
      total_amount: safeDropData.total_amount || 0,
    };

    return cashSettlementApi.createDrawerCount(drawerData);
  },

  // ==================== Current Attendance (v2) ====================

  /**
   * Fetches the current user's attendance (active shift, ss_id, site_id, attendance records).
   * GET /attendance/get-current/v2
   * Handles Lambda-style response with optional "body" wrapper.
   * @param {Object} [options] - Optional parameters
   * @param {string} [options.userId] - User ID (optional, for server-side user lookup)
   * @returns {Promise<Object>} - Attendance data: { active_shift, ss_id, site_id, attendance, ... }
   * @throws {Error} - If token is missing or request fails
   */
  fetchCurrentAttendance: async (options = {}) => {
    const { userId } = options;
    let endpoint = '/attendance/get-current/v2';
    if (userId) {
      endpoint += `?userId=${encodeURIComponent(userId)}`;
    }

    const responseBody = await makeApiRequest(endpoint, 'GET');

    // Handle Lambda-style response: { statusCode, headers, body }
    let attendanceData;
    if (responseBody != null && Object.prototype.hasOwnProperty.call(responseBody, 'body')) {
      const body = responseBody.body;
      if (body !== null && typeof body === 'object') {
        attendanceData = body;
      } else if (typeof body === 'string') {
        try {
          attendanceData = JSON.parse(body);
        } catch (e) {
          throw new Error('Invalid body format in response: body is not valid JSON');
        }
      } else {
        throw new Error('Invalid body format in response');
      }
    } else {
      attendanceData = responseBody;
    }

    return attendanceData;
  },
};

export default cashSettlementApi;
